import React, { useLayoutEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check as CheckIcon, Copy as CopyIcon, Truck as TruckIcon } from "lucide-react";

/* Deterministic barcode: bar and gap widths derived from the value's bytes, so
   every reference draws its own pattern. Decorative (not a scannable symbology) -
   the same visual language as a printed label, generated live. */
export function CodeBars({ value, height = 36, className = "" }) {
  const str = String(value);
  const widths = [2, 1, 1];
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    widths.push(1 + (c % 3), 1 + ((c >> 2) % 2), 1 + ((c >> 4) % 3), 1);
  }
  widths.push(1, 1, 2);
  const total = widths.reduce((a, b) => a + b, 0);
  let x = 0;
  const bars = [];
  widths.forEach((w, i) => {
    if (i % 2 === 0) bars.push(<rect key={i} x={x} y="0" width={w} height="10" />);
    x += w;
  });
  return (
    <svg viewBox={`0 0 ${total} 10`} preserveAspectRatio="none" className={className} style={{ height }} aria-hidden="true">
      <g fill="#0F172A">{bars}</g>
    </svg>
  );
}

/* TicketEndpoint — one end of the route: the pincode as the code, the city beneath. */
export function TicketEndpoint({ label, data, align = "left", size = "md" }) {
  // "lg" is the booked-confirmation ticket: same anatomy, larger pin and a readable city name
  const lg = size === "lg";
  return (
    <div className={`min-w-0 ${align === "right" ? "text-right" : "text-left"}`}>
      <div className={`${lg ? "text-[10.5px]" : "text-[9.5px]"} font-bold uppercase tracking-wider text-slate-600`}>{label}</div>
      {data ? (
        <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}>
          <div className={`font-mono ${lg ? "text-[22px]" : "text-[19px]"} font-semibold leading-tight tracking-[0.04em] tabular-nums text-slate-900`}>{data.pin}</div>
          {data.city ? <div className={`truncate ${lg ? "mt-0.5 text-[15px] font-semibold text-slate-800" : "text-[11.5px] font-medium text-slate-600"}`}>{data.city}</div> : null}
        </motion.div>
      ) : (
        <div className={align === "right" ? "flex flex-col items-end" : ""} aria-hidden="true">
          <div className="cfo-skeleton mt-1.5 h-[17px] w-20 rounded-md" />
          <div className="cfo-skeleton mt-1 h-2.5 w-12 rounded" />
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   ShipmentTicket — the living artifact.
   A straight route line the shipment marker travels as the flow completes;
   clean label/value rows that fill as facts become real; a stamp, a barcode
   strip and the AWB once booked. Never shows anything not entered.
──────────────────────────────────────────────────────────────────────────── */
/* The ticket's true silhouette: rounded top, a semicircular die-cut notch on each
   edge at every perforation, and a sawtooth tear along the bottom - one measured
   path, so the drop-shadow and the backdrop flow through every cut for real. */
export function buildTicketOutline(w, h, ys) {
  const R = 12, N = 7, T = 8, TEETH = 20;
  let d = `M ${R} 0 H ${w - R} A ${R} ${R} 0 0 1 ${w} ${R}`;
  ys.forEach((y) => { d += ` V ${y - N} A ${N} ${N} 0 0 0 ${w} ${y + N}`; });
  d += ` V ${h - T}`;
  const step = w / TEETH;
  for (let i = TEETH - 1; i >= 0; i--) {
    d += ` L ${((i + 0.5) * step).toFixed(2)} ${h} L ${(i * step).toFixed(2)} ${h - T}`;
  }
  [...ys].reverse().forEach((y) => { d += ` V ${y + N} A ${N} ${N} 0 0 0 0 ${y - N}`; });
  d += ` V ${R} A ${R} ${R} 0 0 1 ${R} 0 Z`;
  return d;
}

/* `variant="return"` flips the route: a reverse pickup travels back to the merchant, so the
   customer sits on the right and the shipment marker drives right → left — the mirror image of a
   forward order, readable as "coming back" at a glance. Default is the forward ticket, unchanged. */
export default function ShipmentTicket({ pct = 0, step, stageLabel, origin, destination, rows, cells, charge, settlement, orderRef, booked = false, awb, awbCopied, onCopyAwb, title, variant = "forward" }) {
  const rtl = variant === "return";
  const heading = title || (rtl ? "Your return preview" : "Your shipment preview");
  const t = Math.max(0, Math.min(1, pct / 100));
  // where the moving marker sits along the line, measured from its own start edge
  const along = `calc(5px + ${t} * (100% - 10px))`;
  // the teaching line stays for as long as anything on the pass is still waiting
  const awaitingAnything = !booked && (
    !origin || !destination || !orderRef ||
    (rows || []).some((r) => !r.v) || (cells || []).some((c) => !c.v)
  );
  const clipRef = useRef(null);
  const perfARef = useRef(null);
  const perfBRef = useRef(null);
  const [outline, setOutline] = useState("");
  useLayoutEffect(() => {
    const el = clipRef.current;
    if (!el) return undefined;
    const compute = () => {
      const w = el.offsetWidth, h = el.offsetHeight;
      const ys = [perfARef.current, perfBRef.current]
        .filter(Boolean)
        .map((n) => n.offsetTop + n.offsetHeight / 2);
      if (w && h && ys.length === 2) setOutline(buildTicketOutline(w, h, ys));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, cells, charge, booked, awb]);
  const VEHICLE_SPRING = { type: "spring", stiffness: 90, damping: 16, mass: 1.2, delay: 0.26 };
  return (
    <div
      className="relative"
      style={{ filter: "drop-shadow(0 1px 2px rgba(15,23,42,0.06)) drop-shadow(0 14px 24px rgba(15,23,42,0.09)) drop-shadow(0 22px 34px rgba(30,58,138,0.07))" }}
    >
    <div
      className="relative overflow-hidden rounded-xl border border-[#DEE5EF] bg-white"
      ref={clipRef}
      style={{ clipPath: outline ? `path("${outline}")` : "polygon(0% 0%, 100% 0%, 100% calc(100% - 8px), 97.5% 100%, 95% calc(100% - 8px), 92.5% 100%, 90% calc(100% - 8px), 87.5% 100%, 85% calc(100% - 8px), 82.5% 100%, 80% calc(100% - 8px), 77.5% 100%, 75% calc(100% - 8px), 72.5% 100%, 70% calc(100% - 8px), 67.5% 100%, 65% calc(100% - 8px), 62.5% 100%, 60% calc(100% - 8px), 57.5% 100%, 55% calc(100% - 8px), 52.5% 100%, 50% calc(100% - 8px), 47.5% 100%, 45% calc(100% - 8px), 42.5% 100%, 40% calc(100% - 8px), 37.5% 100%, 35% calc(100% - 8px), 32.5% 100%, 30% calc(100% - 8px), 27.5% 100%, 25% calc(100% - 8px), 22.5% 100%, 20% calc(100% - 8px), 17.5% 100%, 15% calc(100% - 8px), 12.5% 100%, 10% calc(100% - 8px), 7.5% 100%, 5% calc(100% - 8px), 2.5% 100%, 0% calc(100% - 8px))" }}
    >
      <div className="flex items-center justify-between rounded-t-[inherit] border-b border-[#D9E3F4] bg-[rgba(56,119,252,0.07)] px-5 py-2.5">
        <span className="inline-flex items-center gap-2">
          <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#3877FC] opacity-50 [animation-duration:2.6s] motion-reduce:hidden" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#3877FC]" />
          </span>
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#2A59BD]">{heading}</span>
        </span>
        {booked ? (
          <span className="rounded-full bg-emerald-500 px-2.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-white">Booked</span>
        ) : (
          <span className="text-[11px] font-semibold tabular-nums text-slate-500">{stageLabel || `Step ${step} of 5`}</span>
        )}
      </div>

      {awaitingAnything && (
        <p className="px-5 pt-3 text-center text-[10.5px] font-medium text-slate-500">
          Fills in live as you enter the {rtl ? "return" : "shipment"} details
        </p>
      )}

      {/* route — a return reads right → left: the customer on the right, coming back to you */}
      <div className="px-5 pt-3.5">
        <div className="flex items-start justify-between gap-4">
          {rtl ? (
            <>
              <TicketEndpoint label="To you" data={destination} />
              <TicketEndpoint label="From" data={origin} align="right" />
            </>
          ) : (
            <>
              <TicketEndpoint label="From" data={origin} />
              <TicketEndpoint label="To" data={destination} align="right" />
            </>
          )}
        </div>
        <div className="relative mb-1 mt-3 h-9">
          {/* base line between the endpoint dots - draws itself on arrival */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.45, ease: "easeOut", delay: 0.24 }}
            className="absolute left-[5px] right-[5px] top-1/2 h-[2px] origin-left -translate-y-1/2 rounded-full bg-slate-200"
          />
          {/* one tick per leg of the journey */}
          {[1, 2, 3, 4].map((i) => (
            <span
              key={i}
              className={`absolute top-1/2 h-[7px] w-[2px] -translate-x-1/2 -translate-y-1/2 rounded-full transition-colors duration-300 ${
                t >= i / 5 ? (booked ? "bg-emerald-400" : "bg-[#3877FC]") : "bg-slate-300"
              }`}
              style={{ left: `calc(5px + ${i / 5} * (100% - 10px))` }}
              aria-hidden="true"
            />
          ))}
          {/* progress, with the cargo flowing along it (anchored at the origin's edge) */}
          <motion.div
            className={`absolute top-1/2 h-[2px] -translate-y-1/2 rounded-full ${rtl ? "right-[5px]" : "left-[5px]"} ${booked ? "bg-emerald-500" : "cfo-rainbow"}`}
            initial={false}
            animate={{ width: `calc(${t} * (100% - 10px))` }}
            transition={VEHICLE_SPRING}
          />
          {/* endpoint dots: origin filled, destination hollow (emerald once booked) */}
          <span className={`absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full ${rtl ? "right-0" : "left-0"} ${origin ? "bg-[#3877FC]" : "border-2 border-slate-300 bg-white"}`} />
          <span className={`absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full ${rtl ? "left-0" : "right-0"} ${booked ? "bg-emerald-500" : destination ? "border-2 border-[#3877FC] bg-white" : "border-2 border-slate-300 bg-white"}`} />
          {booked ? (
            <motion.span
              className={`absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-emerald-400 ${rtl ? "left-0" : "right-0"}`}
              initial={{ scale: 1, opacity: 0.7 }}
              animate={{ scale: 3.2, opacity: 0 }}
              transition={{ duration: 1.1, ease: "easeOut", delay: 0.4 }}
              aria-hidden="true"
            />
          ) : null}
          {/* the shipment, on its way — a return drives the other way, so the sprite is mirrored */}
          <motion.span
            className="absolute top-1/2 z-10"
            initial={false}
            animate={rtl ? { right: along } : { left: along }}
            transition={VEHICLE_SPRING}
            style={{ transform: rtl ? "translate(50%, -50%)" : "translate(-50%, -50%)" }}
          >
            {booked ? (
              <span className="grid h-7 w-7 place-items-center rounded-full border-2 border-emerald-500 bg-white text-emerald-600 shadow-sm">
                <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} />
              </span>
            ) : (
              <>
                <span className={`wv-ticket-truck block h-[24px] w-[36px] -translate-y-[10px] [filter:drop-shadow(0_1.5px_2px_rgba(15,23,42,0.3))] ${rtl ? "-scale-x-100" : ""}`} aria-hidden="true" />
                <span className="wv-ticket-truck-fallback h-7 w-7 place-items-center rounded-full border-2 border-[#3877FC] bg-white text-[#2A59BD] shadow-sm">
                  <TruckIcon className={`h-3.5 w-3.5 ${rtl ? "-scale-x-100" : ""}`} />
                </span>
              </>
            )}
          </motion.span>
        </div>
      </div>

      <div ref={perfARef} className="relative my-2" aria-hidden="true">
        <div className="mx-4 border-t border-dashed border-[#CBD5E4]" />
      </div>

      {/* facts */}
      <div className="space-y-[9px] px-5 pb-3 pt-1.5">
        {rows.map(({ k, v, mono }, rowIndex) => (
          <div key={k} className="flex items-center justify-between gap-3">
            <span className="flex-shrink-0 text-[12.5px] font-medium text-slate-600">{k}</span>
            {v ? (
              <motion.span
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, ease: "easeOut", delay: 0.2 }}
                className={`cfo-print min-w-0 max-w-[62%] truncate text-right text-[13px] font-semibold tabular-nums text-slate-900 ${mono ? "font-mono text-[12px] font-medium" : ""}`}
                title={typeof v === "string" ? v : undefined}
              >
                {v}
              </motion.span>
            ) : (
              <span className={`cfo-skeleton h-3.5 rounded-md ${["w-24", "w-16", "w-20", "w-12", "w-14", "w-16"][rowIndex % 6]}`} aria-hidden="true" />
            )}
          </div>
        ))}
        {cells && (
          <div className="grid grid-cols-3 divide-x divide-slate-100 border-t border-slate-100 pt-2.5">
            {cells.map(({ k, v, sub }) => (
              <div key={k} className="min-w-0 px-1 text-center">
                <div className="truncate text-[9.5px] font-bold uppercase tracking-wider text-slate-600">{k}</div>
                {v ? (
                  <motion.div
                    initial={{ opacity: 0, y: 3 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, ease: "easeOut", delay: 0.2 }}
                    className="cfo-print mt-0.5 truncate text-[13.5px] font-semibold tabular-nums text-slate-900"
                    title={sub ? `${v} (${sub})` : v}
                  >
                    {v}
                  </motion.div>
                ) : (
                  <div className="cfo-skeleton mx-auto mt-2 h-3.5 w-10 rounded-md" aria-hidden="true" />
                )}
                {v && sub && <div className="truncate text-[9.5px] font-medium text-slate-400">({sub})</div>}
              </div>
            ))}
          </div>
        )}
        {charge && !booked && (
          <div className="mt-1 border-t border-slate-100 pt-2.5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[12.5px] font-semibold text-slate-700">Shipment charge <span className="font-medium text-slate-400">incl. GST</span></span>
              <motion.span initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }} className="text-[15px] font-semibold tabular-nums text-slate-900">
                {charge}
              </motion.span>
            </div>
            {settlement && (
              <p className="mt-1 text-[11px] font-medium leading-relaxed text-slate-400">
                {settlement === "COD net-off" ? <>Will be adjusted against your <span className="text-slate-500">COD net-off</span></> : <>Will be settled from <span className="text-slate-500">{settlement}</span></>}<span className="text-slate-300"> *</span>
              </p>
            )}
            <p className="mt-1.5 flex items-start gap-1 text-[10px] leading-relaxed text-slate-400/90">
              <span aria-hidden="true" className="mt-px text-slate-300">*</span>
              <span>Reflects the latest rates and your current wallet setup. The exact charge and how it settles are confirmed when you book.</span>
            </p>
          </div>
        )}
      </div>

      {/* stub - the tear-off half: the AWB lives here, ghosted until booking prints it */}
      <div ref={perfBRef} className="relative my-1.5" aria-hidden="true">
        <div className="mx-4 border-t border-dashed border-[#CBD5E4]" />
      </div>
      {!booked && (
        <div className="px-5 pb-7 pt-2.5">
          {orderRef ? (
            <motion.div key={orderRef} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, ease: "easeOut" }}>
              <CodeBars value={orderRef} height={36} className="mx-auto block w-[84%]" />
              <p className="mt-2 truncate text-center font-mono text-[11px] font-medium tracking-[0.08em] text-slate-600" title={orderRef}>{orderRef}</p>
            </motion.div>
          ) : (
            <div>
              <div
                className="mx-auto h-9 w-[84%] rounded-[2px] opacity-60"
                aria-hidden="true"
                style={{ background: "repeating-linear-gradient(90deg, #D9E3F2 0 2px, transparent 2px 4px, #D9E3F2 4px 5px, transparent 5px 9px, #D9E3F2 9px 12px, transparent 12px 14px)" }}
              />
              <div className="cfo-skeleton mx-auto mt-2 h-2.5 w-32 rounded" aria-hidden="true" />
            </div>
          )}
        </div>
      )}
      {booked && awb && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut", delay: 0.75 }}
          className="px-5 pb-7 pt-2.5"
        >
          <CodeBars value={awb} height={36} className="mx-auto block w-[84%]" />
          <div className="mt-2.5 flex items-center justify-center gap-2">
            <span className="font-mono text-[16px] font-semibold tracking-[0.06em] text-slate-900">{awb}</span>
            <button
              type="button"
              onClick={onCopyAwb}
              className="grid h-7 w-7 place-items-center rounded-md border border-slate-200 bg-white text-slate-500 transition-colors hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40"
              title="Copy AWB"
            >
              {awbCopied ? <CheckIcon className="h-3.5 w-3.5 text-emerald-600" /> : <CopyIcon className="h-3.5 w-3.5" />}
            </button>
          </div>
          <p className="mt-1 text-center text-[9px] font-semibold uppercase tracking-wider text-slate-500">AWB · for your reference</p>
        </motion.div>
      )}

      {/* stamp */}
      {booked && (
        <motion.div
          initial={{ opacity: 0, scale: 1.6, rotate: -18 }}
          animate={{ opacity: 1, scale: 1, rotate: -12 }}
          transition={{ type: "spring", stiffness: 260, damping: 15, delay: 0.25 }}
          className="pointer-events-none absolute right-4 top-[74px] select-none rounded-md border-2 border-emerald-500/60 px-2 py-0.5 text-[13px] font-bold uppercase tracking-[0.18em] text-emerald-600/80"
          aria-hidden="true"
        >
          Booked
        </motion.div>
      )}
    </div>
    </div>
  );
}
